/**
 * portalClickGuard.ts — Round O (2026-07-15) guest sign-in link fix (O2),
 * widened 2026-09-17 to cover the one global notice band.
 *
 * Several chat surfaces render OUTSIDE the chat container via
 * `ReactDOM.createPortal(..., document.body)` (token-usage ⓘ popups, special
 * tile popups). Document-level mousedown handlers that treat "not inside the
 * chat container" as "outside" would otherwise:
 *   1. close the whole chat pane (useGlobalChat handleClickOutside), and/or
 *   2. unmount the popup before the browser can complete the click
 *      (GlobalChatSystem handleGlobalClick) — which killed the guest
 *      "Sign in" anchor inside the portaled ⓘ popup: mousedown removed the
 *      anchor from the DOM, so the click (and its default /auth navigation)
 *      never happened.
 *
 * Portaled chat surfaces mark their root with `data-prest0n-portal="true"`.
 *
 * NOT EVERY SURFACE THE CHAT MUST NOT CLOSE FOR IS A PORTAL (2026-09-17).
 * GlobalBannerCenter's band is an ordinary App.tsx sibling, and while the
 * FULLSCREEN chat is up it is deliberately the one thing rendered above the
 * takeover — which makes it the only reachable "outside the chat" target in
 * that view. A mousedown on it ran the full failure above, both halves at
 * once: the pane closed (the user was thrown out of the fullscreen chat) and
 * the band un-pinned back into normal flow, moving out from under the pointer
 * before mouseup, so the click on "refresh now" never completed and no
 * refresh happened. It is the SAME defect the ⓘ anchor had, on a surface the
 * portal marker could never name.
 *
 * So there are two questions, and they are deliberately different predicates:
 *
 *   isInsidePrest0nPortal   — "is this a body-portaled CHAT surface?" The
 *                             popup-dismiss handler's question: a click on
 *                             the notice band is a click elsewhere in the app
 *                             and SHOULD dismiss an open ⓘ popup.
 *   isChatSafeClickTarget   — "may this click close the chat pane?" Portals
 *                             plus anything wearing the chat-safe marker.
 *
 * Both live here so the two document-level mousedown paths keep reading their
 * rules from one module, as they have since O2.
 */

export const PREST0N_PORTAL_SELECTOR = '[data-prest0n-portal="true"]';

/**
 * The marker a NON-portal surface wears to say "a click here is not a click
 * outside the chat". Matched BY VALUE, the same way the portal marker is:
 * ThreadArchiveNoticeOverlay shipped `data-prest0n-portal=""` and its scrim
 * closed the chat for months because a presence-only selector never matched.
 */
export const PREST0N_CHAT_SAFE_ATTR = 'data-prest0n-chat-safe';
export const PREST0N_CHAT_SAFE_SELECTOR = `[${PREST0N_CHAT_SAFE_ATTR}="true"]`;

/** Portals AND chat-safe chrome — the union the pane-closing handler consults. */
export const PREST0N_NO_CLOSE_SELECTOR =
  `${PREST0N_PORTAL_SELECTOR}, ${PREST0N_CHAT_SAFE_SELECTOR}`;

/**
 * True when `target` sits inside an element matching `selector`.
 * Accepts any EventTarget: Elements match via `closest`; non-Element nodes
 * (e.g. text nodes) walk to their parent element first; everything else
 * (window/document/null) is not inside anything.
 */
function isInside(target: EventTarget | null, selector: string): boolean {
  if (!target) return false;
  if (target instanceof Element) {
    return !!target.closest(selector);
  }
  if (target instanceof Node && target.parentElement) {
    return !!target.parentElement.closest(selector);
  }
  return false;
}

/** True when the event target sits inside a body-portaled chat surface. */
export function isInsidePrest0nPortal(target: EventTarget | null): boolean {
  return isInside(target, PREST0N_PORTAL_SELECTOR);
}

/**
 * True when a mousedown on this target must NOT close the chat pane — a
 * portaled chat surface, or app chrome that marked itself chat-safe.
 */
export function isChatSafeClickTarget(target: EventTarget | null): boolean {
  return isInside(target, PREST0N_NO_CLOSE_SELECTOR);
}

/** Everything the pane-closing decision depends on, as plain facts. */
export interface OutsideClickContext {
  /** Is the pane open at all? */
  chatOpen: boolean;
  /** Docked right-hand panel. */
  docked: boolean;
  /** Full-screen takeover. */
  fullScreen: boolean;
  insideChat: boolean;
  insidePromptBar: boolean;
  insideFloatingPromptBar: boolean;
  /** isChatSafeClickTarget(event.target) — a portal, or chat-safe chrome. */
  chatSafeTarget: boolean;
}

/**
 * Should this document-level mousedown close the chat pane?
 *
 * Pure so the rule can be exercised directly — the handler that used to hold
 * it lives 3,700 lines into a hook nothing can mount in a unit test, which is
 * how a mode it does not apply to went unnoticed.
 *
 * AUTO-CLOSE IS A FLOATING-OVERLAY BEHAVIOUR ONLY. A floating pane is a
 * transient thing you dismiss by clicking away from it. The other two modes
 * are places the user chose to BE:
 *   - DOCKED is pinned (owner directive 2026-08-22) — it survives clicks and
 *     navigation anywhere in the app; only ✕ / the mode buttons move it.
 *   - FULLSCREEN is the whole viewport, so "outside the chat" is not
 *     somewhere the user can even aim. The single exception is the notice
 *     band deliberately pinned ABOVE the takeover — so the only click this
 *     rule could ever fire on there was one aimed at the band's own controls,
 *     which is exactly what it must not close the chat for (owner report
 *     2026-09-17).
 */
export function shouldCloseChatOnOutsideClick(ctx: OutsideClickContext): boolean {
  if (!ctx.chatOpen) return false;
  if (ctx.docked || ctx.fullScreen) return false;
  if (ctx.insideChat || ctx.insidePromptBar || ctx.insideFloatingPromptBar) return false;
  if (ctx.chatSafeTarget) return false;
  return true;
}
